import { useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { Popover } from '@heroui/react';
import { Tooltip } from '@/components/Tooltip';
import {
  getChannelLevel,
  levelDescription,
  levelLabel,
  setChannelLevel,
  subscribeMuteSettings,
  type NotificationLevel,
} from '@/lib/muteSettings';
import { AtSignIcon, BellIcon, BellOffIcon, VolumeOffIcon } from '@/components/icons';

/** Distinct icon per notification level so each state is recognisable at a
 *  glance (a single bell-off for every non-default level hid the difference
 *  between "no sound", "mentions only" and "fully muted"). */
function levelIcon(level: NotificationLevel) {
  switch (level) {
    case 'ALL':
      return BellIcon;
    case 'ALL_SILENT':
      return VolumeOffIcon;
    case 'MENTIONS':
      return AtSignIcon;
    case 'NOTHING':
      return BellOffIcon;
  }
}

/**
 * 🔔 toggle with a distinct icon per level: bell (all), volume-off (no
 * sound), @ (mentions only), bell-off (nothing). Any non-ALL level is tinted
 * warning so a muted channel stands out from across the screen. Shows the
 * effective level (a muted server mutes its channels) and follows changes
 * made on other devices (lib/muteSettings).
 */
export function MuteButton(props: { channelId: string; guildId: string | null }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const level = useSyncExternalStore(subscribeMuteSettings, () =>
    getChannelLevel(props.channelId, props.guildId),
  );

  const muted = level !== 'ALL';
  const title = muted ? t('lib2.mute.buttonLevel', { level: levelLabel(level).toLowerCase() }) : t('lib2.mute.button');
  const Icon = levelIcon(level);
  return (
    <Popover isOpen={open} onOpenChange={setOpen}>
      <Tooltip label={t('lib2.mute.button')}>
        <Popover.Trigger
          aria-label={title}
          className="group rounded-lg p-2 text-ink-dim transition-colors hover:bg-white/10 hover:text-accent"
        >
          <Icon
            className={'h-[22px] w-[22px] group-hover:[animation:icon-shake_0.4s_ease-in-out_2]' + (muted ? ' text-warning' : '')}
          />
        </Popover.Trigger>
      </Tooltip>
      <Popover.Content className="w-72" placement="bottom end">
        <Popover.Dialog className="p-1.5">
          <ul className="space-y-0.5">
            {(['ALL', 'ALL_SILENT', 'MENTIONS', 'NOTHING'] as NotificationLevel[]).map((opt) => {
              const active = opt === level;
              return (
                <li key={opt}>
                  <button
                    type="button"
                    onClick={(e) => {
                      setChannelLevel(props.channelId, opt);
                      setOpen(false);
                      e.currentTarget.blur();
                    }}
                    data-active={active}
                    aria-pressed={active}
                    className={'relative flex min-h-12 w-full items-start gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-default/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60' + (active ? ' bg-default/50' : '')}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-ink">
                        {levelLabel(opt)}
                      </span>
                      <span className="block text-[13px] leading-snug text-ink-muted">
                        {levelDescription(opt)}
                      </span>
                    </span>
                    {active && (
                      <span className="mt-1 text-xs text-accent" aria-hidden>
                        ✓
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </Popover.Dialog>
      </Popover.Content>
    </Popover>
  );
}
